import React, { useEffect, useRef, useState, useCallback, useMemo, Suspense, forwardRef, useImperativeHandle } from 'react';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import LinkExtension from '@tiptap/extension-link';
import UnderlineExtension from '@tiptap/extension-underline';
import HighlightExtension from '@tiptap/extension-highlight';
import TextAlignExtension from '@tiptap/extension-text-align';
import TextStyleExtension from '@tiptap/extension-text-style';
import FontFamilyExtension from '@tiptap/extension-font-family';
import ImageExtension from '@tiptap/extension-image';
import HorizontalRuleExtension from '@tiptap/extension-horizontal-rule';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';
import Paragraph from '@tiptap/extension-paragraph';
import {
  RichTextEditorProps,
  SlashCommand
} from '@/types';
import { EditorSkeleton } from '../../components/shared/ui/LazyFallbacks';
import { useEditorReady } from '../../hooks/useEditorReady';
import useEditorShortcuts from '../../hooks/useEditorShortcuts';
import { SHORTCUTS, comboFor } from '../../utils/shortcuts';
import { ErrorBoundary } from '../../components/shared/ErrorBoundary';
import TableContextMenu from '../../components/shared/ui/TableContextMenu';
import InstantTip from '../../components/shared/common/InstantTip';
import TableGridPopover from '../../components/shared/ui/TableGridPopover';
import { logger } from '../../utils/logger';
import { UrlInputModal } from '../../components/shared/ui/UrlInputModal';
import { Undo2, Redo2, Bold, Italic, Underline as UnderlineIcon, Minus, Highlighter, List, ListOrdered, AlignLeft, AlignCenter, AlignRight, AlignJustify, Link as LinkIcon } from 'lucide-react';
// For lodash type error, run: npm i --save-dev @types/lodash

// near top of RichTextEditor.tsx
const DEFAULT_DOC = { type: 'doc', content: [{ type: 'paragraph' }] };

function parseTipTapContent(incoming: unknown) {
  if (!incoming) return DEFAULT_DOC;
  if (typeof incoming === 'string') {
    try {
      const parsed = JSON.parse(incoming);
      if (parsed && typeof parsed === 'object') return parsed;   // valid TipTap doc
      // If parsed === "" (empty string), normalize to default doc
      return DEFAULT_DOC;
    } catch {
      // Assume HTML string (not JSON) – TipTap can accept this, but we'll prefer doc objects
      // To avoid JSON text appearing as literal text, be conservative:
      return /^\s*[{[]/.test(incoming) ? DEFAULT_DOC : incoming;
    }
  }
  return incoming; // object already
}

const tip = (action: string): string => {
  const shortcut = SHORTCUTS[action];
  if (!shortcut || !shortcut.combo) {
    return shortcut?.label || action;
  }
  return `${shortcut.label} (${comboFor(action)})`;
};

const toolbarButtonClass = (active = false) =>
  `toolbar-btn${active ? ' toolbar-btn--active' : ''}`;

// Base slash commands (without image handler - will be added in component)
const baseSlashCommands: SlashCommand[] = [
  {
    find: /@horizontalline$/,
    handler: ({ chain, range }) => {
      chain().deleteRange(range).setHorizontalRule().run();
    },
  },
  {
    find: /\/horizontalline$/,
    handler: ({ chain, range }) => {
      chain().deleteRange(range).setHorizontalRule().run();
    },
  },
  {
    find: /\/table$/,
    handler: ({ chain, range }) => {
      chain().deleteRange(range).insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
    },
  },
];

// Custom fallback component for RichTextEditor errors
const RichTextEditorFallback: React.FC<{ error: Error; resetError: () => void }> = React.memo(({ error, resetError }) => {
  const handleReset = useCallback(() => {
    window.location.reload();
  }, []);

  return (
    <div className="p-4 bg-red-50 text-red-700 rounded-lg border border-red-200">
      <h3 className="font-semibold mb-2">Editor Error</h3>
      <p className="text-sm mb-3">{error.message}</p>
      <div className="flex space-x-2">
        <button 
          onClick={resetError}
          className="px-4 py-2 text-sm font-medium text-textInverse bg-accent border border-accent rounded-md transition-colors hover:bg-accentStrong hover:border-accentStrong"
        >
          Try Again
        </button>
        <button 
          onClick={handleReset}
          className="px-4 py-2 text-sm font-medium text-textPrimary bg-card border border-border rounded-md transition-colors hover:bg-tableHover"
        >
          Reset Editor
        </button>
      </div>
    </div>
  );
});

RichTextEditorFallback.displayName = 'RichTextEditorFallback';

// ✅ Expose editor methods via ref
export interface RichTextEditorRef {
  getLatestContent: () => any | null;
}

const RichTextEditor = forwardRef<RichTextEditorRef, RichTextEditorProps>(({
  value = '',
  isInternalUser = true,
  readOnly = false,
  onChange,
  onUpdate,
  className = '',
  showToolbar = true
}, ref) => {
  // State management
  const [isModified, setIsModified] = useState(false);
  const feedbackTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  
  // URL modal state
  const [isImageUrlModalOpen, setIsImageUrlModalOpen] = useState(false);
  const [isLinkUrlModalOpen, setIsLinkUrlModalOpen] = useState(false);
  const pendingImageCallback = useRef<((url: string) => void) | null>(null);
  const pendingLinkCallback = useRef<((url: string) => void) | null>(null);
  
  // Guards to prevent prop↔editor thrash
  const lastAppliedFromProp = useRef<string | null>(null);
  const lastEmittedUp = useRef<string | null>(null);
  
  // ✅ RAF cancellation: Track pending requestAnimationFrame to cancel previous ones during fast typing
  // This prevents multiple RAF callbacks from queuing up and causing redundant saves
  const pendingUpdateRaf = useRef<number | null>(null);
  const pendingBlurRaf = useRef<number | null>(null);
  
  // Store editor instance ref for use in callbacks (separate from DOM ref)
  const editorInstanceRef = useRef<Editor | null>(null);

  // Create SlashCommands with access to modal state
  const SlashCommands = useMemo<SlashCommand[]>(() => [
    ...baseSlashCommands,
    {
      find: /\/image$/,
      handler: ({ range }) => {
        // Store the range for later use when URL is submitted
        const savedRange = range;
        // Store callback and open modal
        // Use editorRef to get the current editor when callback is executed
        pendingImageCallback.current = (url: string) => {
          if (url && editorInstanceRef.current) {
            // Use the current editor's chain instead of the saved chain
            editorInstanceRef.current.chain().deleteRange(savedRange).setImage({ src: url }).run();
          }
        };
        setIsImageUrlModalOpen(true);
      },
    },
  ], []);

  // Editor extensions configuration
  const extensions = useMemo(() => [
    Table.configure({
      resizable: true,
      allowTableNodeSelection: true,
    }),
    TableRow,
    TableHeader,
    TableCell,
    StarterKit.configure({
      horizontalRule: false,
      orderedList: {
        HTMLAttributes: {
          class: 'nested-ordered-list',
        },
        keepMarks: true,
      },
      bulletList: {
        HTMLAttributes: {
          class: 'nested-bullet-list',
        },
        keepMarks: true,
      },
      listItem: {
        HTMLAttributes: {
          class: 'list-item-node',
        },
      },
      paragraph: false,
    }),
    Paragraph.extend({
      addKeyboardShortcuts() {
        return {
          'Mod-Enter': () => this.editor.commands.setParagraph(),
        };
      },
      addInputRules() {
        return SlashCommands;
      },
    }),
    HorizontalRuleExtension,
    UnderlineExtension,
    LinkExtension.configure({
      openOnClick: false,
      HTMLAttributes: {
        target: '_blank',
        rel: 'noopener noreferrer',
        onClick: (e: MouseEvent) => {
          if (!isInternalUser) {
            e.preventDefault();
            const target = e.currentTarget as HTMLAnchorElement;
            const href = target?.href;
            if (href) window.open(href, '_blank');
          }
        }
      }
    }),
    HighlightExtension,
    TextAlignExtension.configure({ types: ['heading', 'paragraph'] }),
    TextStyleExtension,
    FontFamilyExtension.configure({ types: ['textStyle'] }),
    ImageExtension,
  ], [isInternalUser]);

  // Initialize editor
  const editor = useEditor({
    extensions,
    onUpdate: ({ editor }) => {
      // Store editor instance in ref for use in callbacks
      editorInstanceRef.current = editor;
    },
    editorProps: {
      attributes: {
        class: `p-6 min-h-[300px] text-textPrimary focus:outline-none bg-card leading-relaxed`,
        spellCheck: 'true',
      },
      handleDOMEvents: {
        click: (_view, event) => {
          // Always allow link clicks to open in new tab
          const target = event.target as HTMLElement;
          const linkElement = target.closest('a[href]') as HTMLAnchorElement;
          if (linkElement) {
            const href = linkElement.getAttribute('href');
            if (href && (href.startsWith('http://') || href.startsWith('https://') || href.startsWith('mailto:') || href.startsWith('tel:'))) {
              event.preventDefault();
              window.open(href, '_blank', 'noopener,noreferrer');
              return true;
            }
          }
          // For non-internal users or read-only mode, prevent other interactions
          if (!isInternalUser || readOnly) {
            return false;
          }
          return false;
        }
      }
    },
    editable: isInternalUser && !readOnly,
    content: parseTipTapContent(value),      // <-- important
  });

  // Use the custom hook for readiness
  useEditorReady(editor);

  // Set editorReady when editor is editable
  useEffect(() => {
    if (editor?.isEditable) {
      // Editor is ready
    }
  }, [editor]);

  // ✅ CRITICAL FIX: Expose getLatestContent method via ref for unmount cleanup
  // This ensures NotesSection can read the latest content even if handleChange hasn't processed it yet
  useImperativeHandle(ref, () => ({
    getLatestContent: () => {
      if (!editor) return null;
      // ✅ SAFETY: Read directly from editor instance (always current, not stale refs)
      // The editor instance is the source of truth - it always has the latest state
      // This is safe because:
      // 1. Editor instance is stable (created once, destroyed on unmount)
      // 2. getJSON() reads from editor's current state synchronously
      // 3. No stale closure issues since we're reading directly from the instance
      try {
        return editor.getJSON();
      } catch (error) {
        logger.error('Error getting latest content', { error }, 'RichTextEditor');
        return null;
      }
    },
  }), [editor]);

  // Use custom hook for keyboard shortcuts - always call this hook
  useEditorShortcuts(editor);
  
  // Listen for URL modal events (from shortcuts)
  useEffect(() => {
    const handleLinkUrlModal = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail?.onConfirm) {
        pendingLinkCallback.current = customEvent.detail.onConfirm;
        setIsLinkUrlModalOpen(true);
      }
    };
    
    window.addEventListener('open-link-url-modal', handleLinkUrlModal);
    return () => {
      window.removeEventListener('open-link-url-modal', handleLinkUrlModal);
    };
  }, []);

  // Platform detection for keyboard shortcuts
  const isMacLike = useMemo(() => {
    return navigator.platform.toUpperCase().includes('MAC') || 
           navigator.userAgent.toUpperCase().includes('MAC');
  }, []);

  // Directly register keyboard shortcuts for bold, italic, underline, strikethrough, highlight, undo, redo
  useEffect(() => {
    if (!editor?.view) return;
    const editorElement = editor.view.dom;
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle shortcuts when editor is focused and user is internal
      if (!editor.isFocused || !isInternalUser) return;
      
      const isCmdOrCtrl = isMacLike ? e.metaKey : e.ctrlKey;
      const isShift = e.shiftKey;
      
      // Let TipTap's StarterKit handle list deletion naturally
      // The default behavior already merges with previous line correctly
      
      if (isCmdOrCtrl) {
        switch (e.key.toLowerCase()) {
          case 'b':
            e.preventDefault();
            e.stopPropagation();
            editor.chain().focus().toggleBold().run();
            break;
          case 'i':
            e.preventDefault();
            e.stopPropagation();
            editor.chain().focus().toggleItalic().run();
            break;
          case 'u':
            e.preventDefault();
            e.stopPropagation();
            editor.chain().focus().toggleUnderline().run();
            break;
          case 's':
            e.preventDefault();
            e.stopPropagation();
            editor.chain().focus().toggleStrike().run();
            break;
          case 'h':
            e.preventDefault();
            e.stopPropagation();
            editor.chain().focus().toggleHighlight().run();
            break;
          case 'z':
            e.preventDefault();
            e.stopPropagation();
            if (isShift) {
              // CMD/CTRL + SHIFT + Z or CMD/CTRL + Y (Windows) = Redo
              editor.chain().focus().redo().run();
            } else {
              // CMD/CTRL + Z = Undo
              editor.chain().focus().undo().run();
            }
            break;
          case 'y':
            // CMD/CTRL + Y (Windows) = Redo
            if (!isMacLike) {
              e.preventDefault();
              e.stopPropagation();
              editor.chain().focus().redo().run();
            }
            break;
        }
      }
    };
    editorElement.addEventListener('keydown', handleKeyDown);
    return () => {
      editorElement.removeEventListener('keydown', handleKeyDown);
    };
  }, [editor, isInternalUser, isMacLike]);

  // Content validation (removed isEmptyJSON as we always emit doc JSON now)

  // Cleanup timeouts on unmount
  useEffect(() => {
    let timeoutRef = feedbackTimeout.current;
    return () => {
      if (timeoutRef) {
        clearTimeout(timeoutRef);
      }
    };
  }, []);

  // Reset modification state when component unmounts or user navigates away
  useEffect(() => {
    const handleBeforeUnload = () => {
      setIsModified(false);
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        setIsModified(false);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      setIsModified(false);
    };
  }, []);

  useEffect(() => {
    if (!editor) return;

    const incomingRaw = value ?? '';
    // Parse once
    const parsed = parseTipTapContent(incomingRaw);
    const incomingNorm =
      typeof parsed === 'string' ? parsed : JSON.stringify(parsed);

    // If parent is echoing exactly what we already emitted, skip.
    if (incomingNorm === (lastEmittedUp.current ?? '')) return;

    // If we already applied this exact prop payload, skip.
    if (incomingNorm === (lastAppliedFromProp.current ?? '')) return;

    // If editor already holds the same thing, skip.
    const currentNorm = JSON.stringify(editor.getJSON());
    if (incomingNorm === currentNorm) {
      lastAppliedFromProp.current = incomingNorm;
      return;
    }

    // Apply incoming content WITHOUT creating a feedback loop
    if (typeof parsed === 'string') {
      editor.commands.setContent(parsed, false);
    } else {
      editor.commands.setContent(parsed as any, false);
    }

    // Remember that this came from props so we can ignore the resulting 'update'
    lastAppliedFromProp.current = incomingNorm;
    setIsModified(false);
  }, [editor, value]);

  // keep editability in sync after mount
  useEffect(() => {
    if (!editor) return;
    editor.setEditable(isInternalUser && !readOnly);

    // also toggle pointer-events on the actual DOM node
    const dom = editor.view.dom as HTMLElement;
    if (dom) {
      if (!isInternalUser || readOnly) dom.classList.add('pointer-events-none');
      else dom.classList.remove('pointer-events-none');
    }
  }, [editor, isInternalUser, readOnly]);

  const handleUpdate = useCallback(() => {
    if (!editor) return;
    
    // ✅ FIX: Don't fire onChange/onUpdate in read-only mode (prevents errors in viewer mode)
    if (readOnly || !isInternalUser) return;
    
    if (!isModified) setIsModified(true);

    // ✅ CRITICAL FIX: Defer getJSON() until after TipTap has fully processed the update
    // TipTap's 'update' event fires during the transaction, but the editor state might not
    // be fully committed yet. Using requestAnimationFrame ensures we read the final state.
    // This fixes the bug where the last 2 characters are lost.
    
    // ✅ RAF cancellation: Cancel previous RAF if still pending (prevents queued callbacks during fast typing)
    if (pendingUpdateRaf.current !== null) {
      cancelAnimationFrame(pendingUpdateRaf.current);
    }
    
    pendingUpdateRaf.current = requestAnimationFrame(() => {
      pendingUpdateRaf.current = null; // Clear ref after execution
      if (!editor) return;
      
      try {
        const json = editor.getJSON();
        const serialized = JSON.stringify(json);

        // ⛔️ If this update is the direct result of our last prop->setContent, ignore it
        if (serialized === (lastAppliedFromProp.current ?? '')) return;

        // ⛔️ If we already emitted exactly this, ignore
        if (serialized === (lastEmittedUp.current ?? '')) return;

        lastEmittedUp.current = serialized;
        // ✅ Pass the object directly, not the stringified version
        onChange?.(json);
        onUpdate?.(json);
      } catch (error) {
        logger.error('Error saving content', { error }, 'RichTextEditor');
      }
    });
  }, [editor, onChange, onUpdate, isModified, readOnly, isInternalUser]);

  useEffect(() => {
    if (!editor) return;
    editor.on('update', handleUpdate);
    
    // ✅ OPTIMIZATION: Save immediately when editor loses focus (user clicks away)
    // This ensures data is saved before user switches sections or tabs
    // ✅ CRITICAL: Pass shouldFlush=true to bypass debounce on blur
    const handleBlur = () => {
      if (!readOnly && isInternalUser && editor) {
        // ✅ CRITICAL FIX: Defer getJSON() to ensure TipTap has fully processed any pending updates
        // This prevents the last characters from being lost on blur
        
        // ✅ RAF cancellation: Cancel previous blur RAF if still pending
        if (pendingBlurRaf.current !== null) {
          cancelAnimationFrame(pendingBlurRaf.current);
        }
        
        pendingBlurRaf.current = requestAnimationFrame(() => {
          pendingBlurRaf.current = null; // Clear ref after execution
          if (!editor) return;
          try {
            const json = editor.getJSON();
            const serialized = JSON.stringify(json);
            
            // Only save if content actually changed
            if (serialized !== (lastEmittedUp.current ?? '')) {
              lastEmittedUp.current = serialized;
              // ✅ Pass true as second param to flush immediately (bypass debounce)
              // This ensures saves happen before refresh/navigation
              if (typeof onChange === 'function' && onChange.length >= 2) {
                // If onChange accepts 2+ params, pass shouldFlush=true
                (onChange as (content: any, shouldFlush?: boolean) => void)(json, true);
              } else {
                // Fallback for old signature
                onChange?.(json);
              }
              onUpdate?.(json);
            }
          } catch (error) {
            logger.error('Error saving content on blur', { error }, 'RichTextEditor');
          }
        });
      }
    };
    
    // Listen for blur events on the editor DOM
    const editorElement = editor.view.dom;
    editorElement.addEventListener('blur', handleBlur);
    
    return () => {
      editor.off('update', handleUpdate);
      editorElement.removeEventListener('blur', handleBlur);
      
      // ✅ Cleanup: Cancel any pending RAFs on unmount to prevent stale callbacks
      if (pendingUpdateRaf.current !== null) {
        cancelAnimationFrame(pendingUpdateRaf.current);
        pendingUpdateRaf.current = null;
      }
      if (pendingBlurRaf.current !== null) {
        cancelAnimationFrame(pendingBlurRaf.current);
        pendingBlurRaf.current = null;
      }
    };
  }, [editor, handleUpdate, onChange, onUpdate, readOnly, isInternalUser]);

  // Keyboard event handlers for table operations
  useEffect(() => {
    if (!editor?.view) return undefined;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!editor?.isEditable) return;
      
      // Table deletion
      if (e.key === 'Backspace') {
        const { state } = editor;
        const { $from } = state.selection;
        const before = state.doc.resolve(Math.max(0, $from.start() - 2));
        const nodeBefore = before.nodeBefore;
        if (nodeBefore?.type?.name === 'table') {
          e.preventDefault();
          editor.commands.deleteTable();
          return;
        }
      }
      
      // Table navigation
      if (e.key === 'Tab') {
        const { state } = editor;
        const { $from } = state.selection;
        let inTable = false;
        for (let d = $from.depth; d > 0; d--) {
          if ($from.node(d).type.name === 'table') {
            inTable = true;
            break;
          }
        }
        if (inTable) {
          e.preventDefault();
          if (e.shiftKey) editor.commands.goToPreviousCell();
          else editor.commands.goToNextCell();
        }
      }
    };

    const editorElement = editor.view.dom;
    if (editorElement) {
      editorElement.addEventListener('keydown', handleKeyDown);
      return () => {
        editorElement.removeEventListener('keydown', handleKeyDown);
      };
    }
    return undefined;
  }, [editor]);

  // External user handler - only prevent shortcuts for external users
  useEffect(() => {
    if (!editor || isInternalUser) return;
    
    const handler = (e: KeyboardEvent) => {
      // Only prevent shortcuts when editor is focused and user is external
      if (editor.isFocused && (e.metaKey || e.ctrlKey)) {
        // Allow Ctrl+A for select all in view mode
        if (e.key.toLowerCase() !== 'a') {
          e.preventDefault();
          e.stopPropagation();
        }
      }
    };
    
    document.addEventListener('keydown', handler, true);
    return () => document.removeEventListener('keydown', handler, true);
  }, [editor, isInternalUser]);

  // Cleanup feedback timeout on unmount
  useEffect(() => {
    const timeoutRef = feedbackTimeout.current;
    return () => {
      if (timeoutRef) {
        clearTimeout(timeoutRef);
      }
    };
  }, []);

  // Defensive: Don't use editor before it's initialized
  if (!editor) {
    return (
      <div className="p-6 bg-card">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-3/4" />
          <div className="space-y-2">
            <div className="h-4 bg-gray-200 rounded" />
            <div className="h-4 bg-gray-200 rounded w-5/6" />
          </div>
        </div>
      </div>
    );
  }

  // Only wait for the editor instance to exist (not for editable state)
  if (!editor) {
    return (
      <div className="p-6 bg-card">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-3/4" />
          <div className="space-y-2">
            <div className="h-4 bg-gray-200 rounded" />
            <div className="h-4 bg-gray-200 rounded w-5/6" />
          </div>
        </div>
      </div>
    );
  }

  // Debug logging
  logger.debug('Editor state', { hasEditor: !!editor, isEditable: editor?.isEditable, hasView: !!editor?.view }, 'RichTextEditor');

  return (
    <ErrorBoundary fallback={RichTextEditorFallback as React.ComponentType<{ error?: Error; resetError?: () => void }>}>
      <div className={`${['notes-editor', className].filter(Boolean).join(' ')} ${!isInternalUser || readOnly ? 'readonly-mode' : ''}`}>
        <div className="notes-editor-surface relative" ref={editorRef}>
        {showToolbar && isInternalUser && !readOnly && (
            <div className="notes-editor-toolbar">
            <div className="editor-toolbar flex flex-col md:flex-row items-start md:items-center md:justify-between gap-3 md:gap-2">
              {/* Left-aligned toolbar groups */}
              <div className="flex items-center flex-wrap gap-2 min-w-0 w-full md:flex-1">
                {/* Undo/Redo Group */}
                <div className="flex items-center space-x-1 mr-3 toolbar-group">
                  <InstantTip content={isMacLike ? "Undo (⌘Z)" : "Undo (Ctrl+Z)"}>
                    <button
                      onClick={() => editor?.chain().focus().undo().run()}
                      disabled={!editor?.can().undo()}
                      type="button"
                      aria-label="Undo"
                        className={`${toolbarButtonClass()} min-w-[44px] min-h-[44px] md:min-w-0 md:min-h-0 disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      <Undo2 size={16} />
                    </button>
                  </InstantTip>
                  <InstantTip content={isMacLike ? "Redo (⌘⇧Z)" : "Redo (Ctrl+Y)"}>
                    <button
                      onClick={() => editor?.chain().focus().redo().run()}
                      disabled={!editor?.can().redo()}
                      type="button"
                      aria-label="Redo"
                        className={`${toolbarButtonClass()} disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      <Redo2 size={16} />
                    </button>
                  </InstantTip>
                </div>
                <div className="w-px h-6 bg-border mx-2 hidden sm:block" />
                {/* Text Formatting Group */}
                <div className="flex items-center space-x-1 mr-3 toolbar-group">
                  <InstantTip content={isMacLike ? "Bold (⌘B)" : "Bold (Ctrl+B)"}>
                    <button
                      onClick={() => editor?.chain().focus().toggleBold().run()}
                        className={toolbarButtonClass(!!editor?.isActive('bold'))}
                      type="button"
                      aria-label="Bold"
                    >
                      <Bold size={16} />
                    </button>
                  </InstantTip>
                  <InstantTip content={isMacLike ? "Italic (⌘I)" : "Italic (Ctrl+I)"}>
                    <button
                      onClick={() => editor?.chain().focus().toggleItalic().run()}
                        className={toolbarButtonClass(!!editor?.isActive('italic'))}
                      type="button"
                      aria-label="Italic"
                    >
                      <Italic size={16} />
                    </button>
                  </InstantTip>
                  <InstantTip content={isMacLike ? "Underline (⌘U)" : "Underline (Ctrl+U)"}>
                    <button
                      onClick={() => editor?.chain().focus().toggleUnderline().run()}
                        className={toolbarButtonClass(!!editor?.isActive('underline'))}
                      type="button"
                      aria-label="Underline"
                    >
                      <UnderlineIcon size={16} />
                    </button>
                  </InstantTip>
                  <InstantTip content={isMacLike ? "Strikethrough (⌘S)" : "Strikethrough (Ctrl+S)"}>
                    <button
                      onClick={() => editor?.chain().focus().toggleStrike().run()}
                        className={toolbarButtonClass(!!editor?.isActive('strike'))}
                      type="button"
                      aria-label="Strikethrough"
                    >
                      <Minus size={16} />
                    </button>
                  </InstantTip>
                  <InstantTip content={isMacLike ? "Highlight (⌘H)" : "Highlight (Ctrl+H)"}>
                    <button
                      onClick={() => editor?.chain().focus().toggleHighlight().run()}
                        className={toolbarButtonClass(!!editor?.isActive('highlight'))}
                      type="button"
                      aria-label="Highlight"
                    >
                      <Highlighter size={16} />
                    </button>
                  </InstantTip>
                </div>
                  <div className="w-px h-6 bg-border mx-2 hidden sm:block" />
                {/* Paragraph Styles Group */}
                <div className="flex items-center space-x-1 mr-3 toolbar-group">
                  <div className="relative">
                    <select
                      onChange={(e) => {
                        const level = parseInt(e.target.value, 10);
                        if (level === 0) {
                          if (!editor) return;
                          editor.chain().focus().setParagraph().run();
                        } else {
                          if (!editor) return;
                          editor.chain().focus().toggleHeading({ level: level as 1 | 2 | 3 }).run();
                        }
                      }}
                        className="toolbar-select text-xs sm:text-sm"
                      value={editor?.isActive('heading', { level: 1 }) ? '1' : 
                             editor?.isActive('heading', { level: 2 }) ? '2' : 
                             editor?.isActive('heading', { level: 3 }) ? '3' : '0'}
                      aria-label="Text Style"
                    >
                      <option value="0">Text</option>
                      <option value="1">H1</option>
                      <option value="2">H2</option>
                      <option value="3">H3</option>
                    </select>
                  </div>
                </div>
                  <div className="w-px h-6 bg-border mx-2 hidden sm:block" />
                {/* Lists Group */}
                <div className="flex items-center space-x-1 mr-3 toolbar-group">
                  <InstantTip content={tip('bulletList')}>
                    <button
                      onClick={() => editor?.chain().focus().toggleBulletList().run()}
                        className={toolbarButtonClass(!!editor?.isActive('bulletList'))}
                      type="button"
                      aria-label="Bullet List"
                    >
                      <List size={16} />
                    </button>
                  </InstantTip>
                  <InstantTip content={tip('orderedList')}>
                    <button
                      onClick={() => editor?.chain().focus().toggleOrderedList().run()}
                        className={toolbarButtonClass(!!editor?.isActive('orderedList'))}
                      type="button"
                      aria-label="Numbered List"
                    >
                      <ListOrdered size={16} />
                    </button>
                  </InstantTip>
                </div>
                  <div className="w-px h-6 bg-border mx-2 hidden sm:block" />
                {/* Alignment Group */}
                <div className="flex items-center space-x-1 mr-3 toolbar-group">
                  <InstantTip content="Align Left">
                    <button
                      onClick={() => editor?.chain().focus().setTextAlign('left').run()}
                        className={toolbarButtonClass(!!editor?.isActive({ textAlign: 'left' }))}
                      type="button"
                      aria-label="Align Left"
                    >
                      <AlignLeft size={16} />
                    </button>
                  </InstantTip>
                  <InstantTip content="Align Center">
                    <button
                      onClick={() => editor?.chain().focus().setTextAlign('center').run()}
                        className={toolbarButtonClass(!!editor?.isActive({ textAlign: 'center' }))}
                      type="button"
                      aria-label="Align Center"
                    >
                      <AlignCenter size={16} />
                    </button>
                  </InstantTip>
                  <InstantTip content="Align Right">
                    <button
                      onClick={() => editor?.chain().focus().setTextAlign('right').run()}
                        className={toolbarButtonClass(!!editor?.isActive({ textAlign: 'right' }))}
                      type="button"
                      aria-label="Align Right"
                    >
                      <AlignRight size={16} />
                    </button>
                  </InstantTip>
                  <InstantTip content="Justify">
                    <button
                      onClick={() => editor?.chain().focus().setTextAlign('justify').run()}
                        className={toolbarButtonClass(!!editor?.isActive({ textAlign: 'justify' }))}
                      type="button"
                      aria-label="Justify"
                    >
                      <AlignJustify size={16} />
                    </button>
                  </InstantTip>
                </div>
                  <div className="w-px h-6 bg-border mx-2 hidden sm:block" />
                {/* Insert Group */}
                <div className="flex items-center space-x-1 mr-3 toolbar-group">
                  <InstantTip content={tip('link')}>
                    <button
                      onClick={() => {
                        if (!editor) return;
                        // Check if text is selected - require selection before linking
                        const { from, to } = editor.state.selection;
                        const hasSelection = from !== to;
                        
                        if (!hasSelection) {
                          // Show warning or do nothing if no text selected
                          return;
                        }
                        
                        pendingLinkCallback.current = (url: string) => {
                          editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
                        };
                        setIsLinkUrlModalOpen(true);
                      }}
                        className={toolbarButtonClass(!!editor?.isActive('link'))}
                      type="button"
                      aria-label="Link"
                    >
                      <LinkIcon size={16} />
                    </button>
                  </InstantTip>
                  <InstantTip content="Insert Table">
                    <TableGridPopover editor={editor} buttonClassName={toolbarButtonClass()} />
                  </InstantTip>
                  <InstantTip content="Insert Horizontal Line">
                    <button
                      onClick={() => {
                        if (!editor) return;
                        editor.chain().focus().setHorizontalRule().run();
                      }}
                        className={toolbarButtonClass()}
                      type="button"
                      aria-label="Insert Horizontal Line"
                    >
                      <Minus size={16} />
                    </button>
                  </InstantTip>
                </div>
              </div>
              {/* Right-aligned save status */}
              <div className="text-xs text-textMuted flex-shrink-0">
                  {/* intentionally left blank */}
              </div>
            </div>
          </div>
        )}

          <div className="notes-editor-body">
            <Suspense fallback={<EditorSkeleton />}>
          <ErrorBoundary 
            fallback={({ resetError }) => (
              <div className="p-4 bg-red-50 text-red-700 rounded-lg border border-red-200">
                <h3 className="font-semibold mb-2">Content Error</h3>
                <p className="text-sm mb-3">Failed to load editor content</p>
                <button 
                      onClick={() => resetError?.()}
                  className="px-2 py-1 rounded-md bg-card border border-border hover:bg-tableHover text-textPrimary"
                >
                  Reset Editor
                </button>
              </div>
            )}
          >
            <EditorContent editor={editor} />
          </ErrorBoundary>
          {editor && (
            <ErrorBoundary 
              fallback={() => (
                <div className="p-4 bg-red-50 text-red-700">
                  <p>Table operation failed</p>
                  <button onClick={() => window.location.reload()} className="px-2 py-1 rounded-md bg-card border border-border hover:bg-tableHover text-textPrimary">
                    Reload Editor
                  </button>
                </div>
              )}
            >
              <TableContextMenu editor={editor} />
            </ErrorBoundary>
          )}
            </Suspense>
          </div>
        </div>
      </div>
      
      {/* URL Input Modals */}
      <UrlInputModal
        isOpen={isImageUrlModalOpen}
        onClose={() => {
          setIsImageUrlModalOpen(false);
          pendingImageCallback.current = null;
        }}
        onSubmit={(url) => {
          if (pendingImageCallback.current) {
            pendingImageCallback.current(url);
            pendingImageCallback.current = null;
          }
          setIsImageUrlModalOpen(false);
        }}
        title="Enter Image URL"
        label="Image URL"
        placeholder="https://example.com/image.jpg"
      />
      
      <UrlInputModal
        isOpen={isLinkUrlModalOpen}
        onClose={() => {
          setIsLinkUrlModalOpen(false);
          pendingLinkCallback.current = null;
        }}
        onSubmit={(url) => {
          if (pendingLinkCallback.current) {
            pendingLinkCallback.current(url);
            pendingLinkCallback.current = null;
          }
          setIsLinkUrlModalOpen(false);
        }}
        title="Enter Link URL"
        label="Link URL"
        placeholder="https://example.com"
      />
    </ErrorBoundary>
  );
});

RichTextEditor.displayName = 'RichTextEditor';

// ✅ Wrap with React.memo for performance (forwardRef components can be memoized)
export default React.memo(RichTextEditor);